import React from 'react';
import { Text, ProgressIndicator, useIsMobile } from '@amzn/storm-ui';
import AdaptiveHelpTip from '@amzn/storm-ui-adaptive-helptip';
import {
  Bar,
  BAR_STATE,
  BAR_STATE_STYLE,
  getBarStatus,
  POSITION,
} from '../types';
import { BarText, BarStyleType, ProgressIndicatorWrap } from './styles';
import { fontSize } from '../utils';
import { FlexContainer } from '../../../../../commons/components/FlexContainer';

interface LabelAndProgressIndicatorProps {
  label?: string;
  percentage: number;
  state?: BAR_STATE;
  targetLine?: number;
  isMobile?: boolean;
}
interface EndLabelAndHelptipProps {
  content?: string;
  tooltipText?: Array<string>;
  tooltipPosition?: POSITION;
}

const getBarStatusWithTargetLine = (
  percentage: number,
  state: BAR_STATE | undefined,
  targetLine: number | undefined,
): BAR_STATE_STYLE => {
  if (state) return getBarStatus(state);
  if (targetLine && percentage < targetLine) return BAR_STATE_STYLE.ERROR;
  return BAR_STATE_STYLE.DEFAULT;
};

const LabelAndProgressIndicator: React.VFC<LabelAndProgressIndicatorProps> = ({
  label,
  percentage,
  state,
  targetLine,
  isMobile,
}) => (
  <>
    <BarText
      styleType={BarStyleType.LABEL}
      textColor="secondary"
      fontSize={fontSize()}
      inline={true}
    >
      {label}:
    </BarText>
    <ProgressIndicatorWrap>
      <ProgressIndicator
        inline={true}
        value={percentage}
        status={getBarStatusWithTargetLine(percentage, state, targetLine)}
        targets={targetLine ? [targetLine] : undefined}
        size={isMobile ? 'small' : 'mini'}
      />
    </ProgressIndicatorWrap>
  </>
);

const EndLabelAndHelptip: React.VFC<EndLabelAndHelptipProps> = ({
  content,
  tooltipText,
  tooltipPosition,
}) => {
  const textFontSize = fontSize();
  return (
    <>
      <BarText
        styleType={BarStyleType.CONTENT}
        textColor="base"
        fontSize={textFontSize}
        inline={true}
      >
        {content}
      </BarText>
      {tooltipText && (
        <AdaptiveHelpTip
          tooltipProps={{ withCloseButton: false, position: tooltipPosition }}
        >
          {tooltipText.map((text) => (
            <Text fontSize={textFontSize} key={text}>
              {text}
            </Text>
          ))}
        </AdaptiveHelpTip>
      )}
    </>
  );
};

const AttributesBarMobile: React.VFC<Bar> = ({
  label,
  tooltipPosition,
  content,
  percentage,
  targetLine,
  tooltipText,
  state,
}) => (
  <>
    <FlexContainer flexWrap="wrap">
      <LabelAndProgressIndicator
        label={label}
        percentage={percentage}
        state={state}
        targetLine={targetLine}
        isMobile={true}
      />
    </FlexContainer>
    <EndLabelAndHelptip
      content={content}
      tooltipText={tooltipText}
      tooltipPosition={tooltipPosition}
    />
  </>
);

const AttributesBarDesktop: React.VFC<Bar> = ({
  label,
  tooltipPosition,
  content,
  percentage,
  targetLine,
  tooltipText,
  state,
}) => (
  <>
    <LabelAndProgressIndicator
      label={label}
      percentage={percentage}
      state={state}
      targetLine={targetLine}
      isMobile={false}
    />
    <EndLabelAndHelptip
      content={content}
      tooltipText={tooltipText}
      tooltipPosition={tooltipPosition}
    />
  </>
);

export const AttributesBar: React.VFC<Bar> = (props) => {
  const isMobile = useIsMobile();
  if (!props.percentage) return null;
  if (isMobile) return <AttributesBarMobile {...props} />;
  return <AttributesBarDesktop {...props} />;
};
